import React, { useCallback, useEffect, useState } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import { invalidatePLCategoryCache } from '../utils/plCategoryResolver';
import { EXCLUSION_REASONS } from '../utils/pnlExclusions';
import useHideMobileNav from '../utils/useHideMobileNav';
import PNLCategoryMappingPanel from './PNLCategoryMapping';

const BROWN = '#5A4A42';
const DARK_BROWN = '#3E2723';
const CREAM = '#FAF6F2';
const LIGHT_BORDER = '#E8DDD0';
const MUTED = '#8B7B73';
const FONT = "'Montserrat', sans-serif";

const WIZARD_KEY = (sid) => `pnl_wizard_state_${sid}`;
const SEED_KEY = (sid) => `pnl_seed_values_${sid}`;
const TRACKING_KEY = (sid) => `pnl_sales_tracking_${sid}`;

function loadWizardState(sid) {
  try { return JSON.parse(localStorage.getItem(WIZARD_KEY(sid))); } catch { return null; }
}
function saveWizardState(sid, s) {
  try { localStorage.setItem(WIZARD_KEY(sid), JSON.stringify(s)); } catch {}
}
function clearWizardState(sid) {
  try { localStorage.removeItem(WIZARD_KEY(sid)); } catch {}
}

export function hasInProgressWizard(storeId) {
  return !!loadWizardState(storeId);
}

function StepIndicator({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.4rem' }}>
      {[1, 2].map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && <div style={{ flex: 1, height: 2, background: step > 1 ? BROWN : LIGHT_BORDER, borderRadius: 1 }} />}
          <div style={{
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
            background: n <= step ? BROWN : LIGHT_BORDER,
            color: n <= step ? CREAM : MUTED,
            border: n === step ? `2px solid ${DARK_BROWN}` : '2px solid transparent',
            boxSizing: 'border-box',
          }}>
            {n < step ? '✓' : n}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function ChoiceCard({ selected, onClick, icon, title, subtitle }) {
  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', textAlign: 'left', padding: '0.9rem 1.1rem', marginBottom: 10,
      border: `2px solid ${selected ? BROWN : LIGHT_BORDER}`,
      borderRadius: 12, background: selected ? '#F5EDE5' : CREAM,
      cursor: 'pointer', fontFamily: FONT, transition: 'border-color 0.15s, background 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: DARK_BROWN, marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: '0.78rem', color: MUTED, lineHeight: 1.4 }}>{subtitle}</div>
        </div>
        <div style={{ flexShrink: 0, marginTop: 4 }}>
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            border: `2px solid ${selected ? BROWN : '#D4C4B0'}`,
            background: selected ? BROWN : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: CREAM }} />}
          </div>
        </div>
      </div>
    </button>
  );
}

// Match Clover "selling" categories — both menu-item categories AND revenue
// classes (the financial "class" field) — to Countertop P&L categories so the
// EOP sales sync lands each line item in the right cell. Mappings are saved
// immediately to clover_category_mappings (mapping_source = 'manual').
//
// Optional exclusion support (used by the EOP Category Mapping step and the
// Settings → P&L Category Mapping page): pass `exclusions` (map of
// "type:cloverId" → reason) and `onExclusionChange(key, reason|null)` and the
// dropdown grows an "Excluded" option group so irrelevant categories (tax,
// tips, gift cards…) can be explicitly resolved instead of left unmapped.
// `onMappingsChanged` fires after any successful save/delete so parents can
// recheck mapping completeness. When these props are omitted the component
// behaves exactly as before.
export function CloverSalesCategoryMapping({ storeId, pnlCats, exclusions = null, onExclusionChange = null, onMappingsChanged = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuCats, setMenuCats] = useState([]);
  const [revClasses, setRevClasses] = useState([]);
  const [revClassStatus, setRevClassStatus] = useState(null); // 'loaded'|'none_configured'|'permission_denied'|'failed'|null
  const [mapping, setMapping] = useState({}); // `${type}:${id}` -> pnlCategoryId (string)
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated.');
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/clover-selling-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_KEY,
        },
        body: JSON.stringify({ store_id: storeId }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || 'Failed to load Clover categories.');
      setMenuCats(result.menu_categories || []);
      setRevClasses(result.revenue_classes || []);
      setRevClassStatus(result.revenue_class_status || null);
      const m = {};
      (result.mappings || []).forEach((x) => {
        if (x.pnl_category_id) m[`${x.category_type}:${x.clover_category_id}`] = String(x.pnl_category_id);
      });
      setMapping(m);
    } catch (e) {
      setError(e.message || 'Failed to load Clover categories.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { if (storeId) load(); }, [storeId, load]);

  const setMappingFor = async (type, cat, pnlId) => {
    const key = `${type}:${cat.id}`;
    setSavingKey(key);
    setError('');
    try {
      if (!pnlId) {
        await supabase.from('clover_category_mappings').delete()
          .eq('store_id', storeId)
          .eq('category_type', type)
          .eq('clover_category_id', cat.id);
        setMapping((p) => { const n = { ...p }; delete n[key]; return n; });
      } else {
        const { error: upErr } = await supabase.from('clover_category_mappings').upsert({
          store_id: storeId,
          clover_category_id: cat.id,
          clover_category_name: cat.name,
          category_type: type,
          pnl_category_id: Number(pnlId),
          mapping_source: 'manual',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'store_id,clover_category_id' });
        if (upErr) throw upErr;
        setMapping((p) => ({ ...p, [key]: String(pnlId) }));
      }
      if (typeof onMappingsChanged === 'function') onMappingsChanged();
    } catch (e) {
      setError(e.message || 'Failed to save mapping.');
    } finally {
      setSavingKey(null);
    }
  };

  // Value encoding for the exclusion-aware dropdown: P&L ids are plain
  // strings; exclusion reasons are "__excl:<reason>".
  const handleSelectChange = async (type, cat, rawValue) => {
    const key = `${type}:${cat.id}`;
    if (rawValue.startsWith('__excl:')) {
      const reason = rawValue.slice('__excl:'.length);
      // An excluded category must not stay mapped — remove any mapping first.
      await setMappingFor(type, cat, '');
      if (typeof onExclusionChange === 'function') onExclusionChange(key, reason);
      return;
    }
    if (exclusions && exclusions[key] && typeof onExclusionChange === 'function') {
      onExclusionChange(key, null);
    }
    await setMappingFor(type, cat, rawValue);
  };

  const selStyle = {
    padding: '4px 8px', borderRadius: 6, border: `1px solid ${LIGHT_BORDER}`,
    fontSize: '0.78rem', fontFamily: FONT, background: '#fff', minWidth: 150,
  };

  const renderGroup = (label, hint, type, list) => {
    return (
      <div style={{ marginBottom: '0.9rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: '0.72rem', color: MUTED, marginBottom: 6 }}>{hint}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((cat) => {
            const key = `${type}:${cat.id}`;
            const excludedReason = exclusions ? exclusions[key] : null;
            const val = excludedReason ? `__excl:${excludedReason}` : (mapping[key] || '');
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 140px', fontSize: '0.82rem', color: DARK_BROWN, fontWeight: 600 }}>{cat.name}</span>
                <span style={{ fontSize: '0.74rem', color: MUTED }}>→</span>
                <select
                  value={val}
                  disabled={savingKey === key}
                  onChange={(e) => handleSelectChange(type, cat, e.target.value)}
                  style={selStyle}
                >
                  <option value="">{exclusions ? '— Not assigned —' : '— Not tracked —'}</option>
                  {pnlCats.map((pc) => (
                    <option key={pc.id} value={String(pc.id)}>{pc.name}</option>
                  ))}
                  {exclusions && (
                    <optgroup label="Excluded">
                      {EXCLUSION_REASONS.map((r) => (
                        <option key={r.value} value={`__excl:${r.value}`}>{r.label}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRevClassSection = () => {
    if (revClasses.length > 0) {
      return renderGroup(
        'Revenue classes',
        "Clover\u2019s financial/accounting \u201cclass\u201d \u2014 used by some merchants instead of menu categories.",
        'revenue_class',
        revClasses,
      );
    }
    let msg = null;
    if (revClassStatus === 'permission_denied') {
      msg = "Countertop doesn\u2019t have permission to read revenue classes from Clover. Reconnect Clover (in Settings \u2192 Integrations) to grant inventory access, then refresh.";
    } else if (revClassStatus === 'failed') {
      msg = 'Revenue classes couldn\u2019t be loaded from Clover. Try refreshing, or reconnect Clover if the problem persists.';
    } else if (revClassStatus === 'none_configured') {
      msg = 'No revenue classes are configured in Clover for this merchant.';
    }
    return (
      <div style={{ marginBottom: '0.9rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
          Revenue classes
        </div>
        <div style={{ fontSize: '0.72rem', color: MUTED, marginBottom: 4 }}>
          Clover&apos;s financial/accounting &ldquo;class&rdquo; &mdash; used by some merchants instead of menu categories.
        </div>
        {msg && (
          <div style={{ fontSize: '0.78rem', color: MUTED, fontStyle: 'italic' }}>{msg}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: '1rem', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 12, padding: '1rem', background: '#F7FAFF' }}>
      <div style={{ fontWeight: 700, color: '#1a4a7a', fontSize: '0.9rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>🍀</span> Match your Clover sales categories
      </div>
      <p style={{ fontSize: '0.78rem', color: MUTED, marginBottom: '0.8rem', lineHeight: 1.5 }}>
        Clover organizes sales by <strong>menu categories</strong> and/or <strong>revenue classes</strong> (the
        financial "class"). Match the ones you use to your P&L categories so End of Period sales sync into the
        right rows. Leave anything you don't track as "Not tracked".
      </p>

      {error && (
        <div style={{ background: '#FDECEA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.7rem', fontSize: '0.78rem', color: '#721C24' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: MUTED, fontSize: '0.8rem', padding: '0.5rem 0' }}>Loading Clover categories…</div>
      ) : (menuCats.length === 0 && revClasses.length === 0 && !revClassStatus) ? (
        <div style={{ color: MUTED, fontSize: '0.8rem' }}>
          No Clover menu categories or revenue classes were found for this merchant.
        </div>
      ) : (
        <>
          {menuCats.length > 0
            ? renderGroup('Menu item categories', 'How items are grouped on your Clover register/menu.', 'menu_category', menuCats)
            : (
              <div style={{ marginBottom: '0.9rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Menu item categories
                </div>
                <div style={{ fontSize: '0.78rem', color: MUTED, fontStyle: 'italic' }}>No menu item categories found for this merchant.</div>
              </div>
            )
          }
          {renderRevClassSection()}
          <button type="button" onClick={load}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a4a7a', fontSize: '0.74rem', textDecoration: 'underline', padding: 0, fontFamily: FONT }}>
            ↻ Refresh from Clover
          </button>
        </>
      )}
    </div>
  );
}

function SeedValuesForm({ pnlCats, seedValues, onChange }) {
  const inp = {
    padding: '4px 8px', borderRadius: 6, border: `1px solid ${LIGHT_BORDER}`,
    fontSize: '0.78rem', width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: FONT,
  };
  return (
    <div>
      <p style={{ fontSize: '0.8rem', color: MUTED, marginBottom: '0.9rem', lineHeight: 1.5 }}>
        Optional: enter typical sales figures per category as defaults for your next End of Period report.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520, fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: '#FAF3EC' }}>
              {['P&L Category', 'Gross Sales ($)', 'Discounts ($)', 'Comps ($)', 'Recipe Cost %'].map(h => (
                <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: DARK_BROWN, fontWeight: 700, borderBottom: `2px solid ${LIGHT_BORDER}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pnlCats.map((cat, i) => {
              const v = seedValues[cat.id] || {};
              return (
                <tr key={cat.id} style={{ background: i % 2 === 0 ? '#FFFDF9' : CREAM, borderBottom: `1px solid ${LIGHT_BORDER}` }}>
                  <td style={{ padding: '6px 10px', fontWeight: 600, color: DARK_BROWN }}>{cat.name}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={v.grossSales ?? ''}
                      onChange={e => onChange(cat.id, 'grossSales', e.target.value)} style={inp} />
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={v.discounts ?? ''}
                      onChange={e => onChange(cat.id, 'discounts', e.target.value)} style={inp} />
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={v.comps ?? ''}
                      onChange={e => onChange(cat.id, 'comps', e.target.value)} style={inp} />
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <input type="number" min="0" max="100" step="0.1" placeholder="e.g. 28" value={v.recipeCostPct ?? ''}
                      onChange={e => onChange(cat.id, 'recipeCostPct', e.target.value)} style={inp} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WizardModal({ children, onClose }) {
  useHideMobileNav(true);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(62,39,35,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
      }}
    >
      <div style={{
        background: CREAM, borderRadius: 16,
        boxShadow: '0 8px 48px rgba(62,39,35,0.28)',
        width: '100%', maxWidth: 700, maxHeight: '92vh',
        overflowY: 'auto', padding: '1.5rem',
        position: 'relative', boxSizing: 'border-box',
        fontFamily: FONT,
      }}>
        {children}
      </div>
    </div>
  );
}

const PNLSetupWizard = ({ storeId: propStoreId, onComplete, onClose, isRerun = false, startStep = null }) => {
  const [storeId, setStoreId] = useState(propStoreId || null);
  const [step, setStep] = useState(1);
  const mappingOnly = startStep === 'mapping';
  const [categoryModel, setCategoryModel] = useState(mappingOnly ? 'different' : null);
  const [salesTracking, setSalesTracking] = useState(null);
  const [skipUnmapped, setSkipUnmapped] = useState(false);
  const [seedValues, setSeedValues] = useState({});

  const [pnlCats, setPnlCats] = useState([]);
  const [invCats, setInvCats] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Resolved/unresolved status reported by the embedded assignment panel
  // (drives the "different"-model Next/Done gate).
  const [panelStatus, setPanelStatus] = useState(null);

  useEffect(() => {
    if (!storeId) {
      const sid = Number(localStorage.getItem('selectedStoreId'));
      if (sid) setStoreId(sid);
    }
  }, []);

  useEffect(() => {
    if (storeId) loadData(storeId);
  }, [storeId]);

  const loadData = async (sid) => {
    setLoading(true);
    setError('');
    try {
      const [pnlRes, invRes] = await Promise.all([
        supabase.from('pnl_categories').select('id,name,sort_order').eq('store_id', sid).order('sort_order', { ascending: true }),
        supabase.from('inventory_items').select('category').eq('store_id', sid).not('category', 'is', null),
      ]);
      if (pnlRes.error) throw pnlRes.error;
      setPnlCats(pnlRes.data || []);
      const cats = [...new Set((invRes.data || []).map(r => r.category).filter(Boolean))].sort();
      setInvCats(cats);

      const persisted = loadWizardState(sid);
      if (persisted) {
        if (mappingOnly) {
          // Force mapping workspace entry when invoked as the EOP mapping
          // interrupt: ignore any saved step / categoryModel / salesTracking
          // so the user always lands on step 1 with the mapping workspace.
          setStep(1);
          setCategoryModel('different');
        } else {
          if (persisted.step) setStep(persisted.step);
          if (persisted.categoryModel) setCategoryModel(persisted.categoryModel);
          if (persisted.salesTracking) setSalesTracking(persisted.salesTracking);
        }
        if (persisted.skipUnmapped != null) setSkipUnmapped(persisted.skipUnmapped);
        if (persisted.seedValues) setSeedValues(persisted.seedValues);
      }
    } catch (e) {
      setError(e.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!storeId || loading) return;
    saveWizardState(storeId, { step, categoryModel, salesTracking, skipUnmapped, seedValues });
  }, [storeId, step, categoryModel, salesTracking, skipUnmapped, seedValues, loading]);

  // The assignment panel reports every inventory/Clover category as assigned or
  // excluded once mapping is complete. It emits { unmappedInventory, unmappedClover }.
  const panelResolved = !!panelStatus
    && (panelStatus.unmappedInventory?.length ?? 0) === 0
    && (panelStatus.unmappedClover?.length ?? 0) === 0;

  const step1NextEnabled = categoryModel !== null && (
    categoryModel === 'same' ||
    (categoryModel === 'different' && (panelResolved || skipUnmapped))
  );

  const handleNext = async (explicitModel) => {
    if (step !== 1) return;
    const model = explicitModel !== undefined ? explicitModel : categoryModel;
    if (model === 'same') {
      setSaving(true);
      setError('');
      try {
        const allCats = [...pnlCats];
        let maxOrder = pnlCats.reduce((m, c) => Math.max(m, c.sort_order || 0), -1);
        const toInsert = invCats.filter(n => !allCats.some(c => c.name.toLowerCase() === n.toLowerCase()));
        if (toInsert.length > 0) {
          const rows = toInsert.map((n, i) => ({ store_id: storeId, name: n, sort_order: maxOrder + 1 + i }));
          const { data: inserted, error: err } = await supabase.from('pnl_categories').insert(rows).select();
          if (err) throw err;
          (inserted || []).forEach(r => allCats.push(r));
        }
        setPnlCats(allCats);

        const invCatNames = invCats.filter(n => allCats.some(c => c.name.toLowerCase() === n.toLowerCase()));
        if (invCatNames.length > 0) {
          await supabase.from('pnl_category_mappings').delete().eq('store_id', storeId).in('inventory_category', invCatNames);
          const mappingRows = invCatNames.map(n => ({
            store_id: storeId,
            pnl_category_id: allCats.find(c => c.name.toLowerCase() === n.toLowerCase()).id,
            inventory_category: n,
          }));
          await supabase.from('pnl_category_mappings').insert(mappingRows);
        }
        invalidatePLCategoryCache(storeId);
      } catch (e) {
        setError(e.message || 'Failed to set up categories.');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (model === 'different') {
      // Categories may have been added or changed in the assignment panel;
      // refresh so Step 2's seed form reflects the current P&L categories.
      try {
        const { data } = await supabase
          .from('pnl_categories')
          .select('id,name,sort_order')
          .eq('store_id', storeId)
          .order('sort_order', { ascending: true });
        if (data) setPnlCats(data);
      } catch { /* non-fatal — Step 2 seed form will still render existing cats */ }
    }
    setStep(2);
  };

  const handleDone = async (explicitTracking) => {
    setSaving(true);
    setError('');
    try {
      const seedToStore = salesTracking === 'enter' && Object.keys(seedValues).length > 0 ? seedValues : null;

      const { data: storeData } = await supabase
        .from('stores')
        .select('inventory_period_config')
        .eq('id', storeId)
        .maybeSingle();
      const existingConfig = storeData?.inventory_period_config || {};
      // In mapping-only mode (EOP interrupt), do not touch the store's
      // pnl_tracking_mode — only categories/mappings should change.
      const trackingMode = mappingOnly
        ? (existingConfig.pnl_tracking_mode || null)
        : ((explicitTracking !== undefined ? explicitTracking : salesTracking) || 'inventory_only');
      const updatedConfig = {
        ...existingConfig,
        ...(mappingOnly ? {} : { pnl_tracking_mode: trackingMode }),
        ...(seedToStore ? { pnl_seed_values: seedToStore } : {}),
      };
      await supabase.from('stores').update({ inventory_period_config: updatedConfig }).eq('id', storeId);

      if (!mappingOnly && trackingMode) {
        try { localStorage.setItem(TRACKING_KEY(storeId), trackingMode); } catch {}
      }
      if (seedToStore) { try { localStorage.setItem(SEED_KEY(storeId), JSON.stringify(seedToStore)); } catch {} }

      invalidatePLCategoryCache(storeId);
      clearWizardState(storeId);
      if (typeof onComplete === 'function') onComplete();
    } catch (e) {
      setError(e.message || 'Failed to complete setup.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardModal onClose={() => typeof onClose === 'function' && onClose()}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.1rem' }}>
        <div>
          <h4 style={{ margin: 0, color: DARK_BROWN, fontWeight: 800, fontSize: '1.08rem' }}>
            {isRerun ? 'P&L Setup — Re-run Wizard' : 'P&L Category Setup Wizard'}
          </h4>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.76rem', color: MUTED }}>
            Map your inventory subcategories to P&L reporting buckets.
          </p>
        </div>
        <button onClick={() => typeof onClose === 'function' && onClose()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: MUTED, padding: '0 0.2rem', lineHeight: 1, flexShrink: 0 }}
          aria-label="Close wizard">
          ✕
        </button>
      </div>

      <StepIndicator step={step} />

      {error && (
        <div style={{ background: '#FDECEA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '0.55rem 0.8rem', marginBottom: '0.9rem', fontSize: '0.8rem', color: '#721C24' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: MUTED }}>Loading…</div>
      ) : step === 1 ? (
        <div>
          <div style={{ fontWeight: 700, color: DARK_BROWN, marginBottom: '0.8rem', fontSize: '0.92rem' }}>
            Step 1: How should P&L categories work?
          </div>

          {!mappingOnly && (
            <ChoiceCard
              selected={categoryModel === 'same'}
              onClick={() => { setCategoryModel('same'); setSkipUnmapped(false); handleNext('same'); }}
              icon="📋"
              title="Same as my item categories"
              subtitle={`Automatically creates one P&L bucket per inventory subcategory${invCats.length > 0 ? ' — ' + invCats.slice(0, 3).join(', ') + (invCats.length > 3 ? '…' : '') : ' (no subcategories found yet)'}. Fastest to set up.`}
            />
          )}
          {!mappingOnly && (
            <ChoiceCard
              selected={categoryModel === 'different'}
              onClick={() => setCategoryModel('different')}
              icon="🗂"
              title="Different — I'll define custom P&L categories"
              subtitle="Create named P&L buckets (Food, Liquor, Wine…) and assign each inventory category to the one it should report under."
            />
          )}

          {categoryModel === 'different' && (
            <div style={{ marginTop: '1rem', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 12, padding: '1rem' }}>
              {/* Row-based assignment panel for Countertop inventory categories
                  only. Clover selling categories are intentionally excluded
                  here — classifying sold Clover items into P&L categories is a
                  separate later step, handled when sales data is imported and
                  reviewed. The panel manages its own loading, persistence, and
                  exclusions, and reports unresolved categories so this wizard
                  can gate the Next/Done button. */}
              <PNLCategoryMappingPanel
                storeId={storeId}
                includeClover={false}
                onStatusChange={setPanelStatus}
                showIntro
              />
              {!panelResolved && !skipUnmapped && (
                <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                  <button type="button" onClick={() => setSkipUnmapped(true)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '0.76rem', textDecoration: 'underline', fontFamily: FONT }}>
                    Skip unassigned categories — continue anyway
                  </button>
                </div>
              )}
              {skipUnmapped && !panelResolved && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.76rem', color: '#856404', background: '#FFF8DC', border: '1px solid #FFE082', borderRadius: 8, padding: '0.4rem 0.65rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Unassigned categories will appear as "Unmapped" in reports.</span>
                  <button onClick={() => setSkipUnmapped(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#856404', fontSize: '0.73rem', textDecoration: 'underline', padding: 0, fontFamily: FONT }}>
                    undo
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 700, color: DARK_BROWN, marginBottom: '0.8rem', fontSize: '0.92rem' }}>
            Step 2: How will you track sales?
          </div>

          <ChoiceCard
            selected={salesTracking === 'enter'}
            onClick={() => setSalesTracking('enter')}
            icon="💰"
            title="Enter sales data for COGS reporting"
            subtitle="You'll enter gross sales, comps, and discounts per P&L category when creating End of Period reports. Enables COGS % calculations."
          />
          <ChoiceCard
            selected={salesTracking === 'inventory_only'}
            onClick={() => { setSalesTracking('inventory_only'); handleDone('inventory_only'); }}
            icon="📦"
            title="Inventory usage only"
            subtitle="Track usage without sales data. You can always add sales figures later in the End of Period report."
          />

          {salesTracking === 'enter' && pnlCats.length > 0 && (
            <div style={{ marginTop: '1rem', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 12, padding: '1rem' }}>
              <SeedValuesForm
                pnlCats={pnlCats}
                seedValues={seedValues}
                onChange={(catId, field, val) => setSeedValues(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [field]: val } }))}
              />
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.4rem', paddingTop: '1rem', borderTop: `1px solid ${LIGHT_BORDER}` }}>
        <button type="button"
          onClick={step === 1 ? () => typeof onClose === 'function' && onClose() : () => setStep(1)}
          style={{ background: 'none', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: '0.84rem', color: MUTED, fontFamily: FONT }}>
          {step === 1 ? 'Cancel' : '← Back'}
        </button>

        {step === 1 ? (
          mappingOnly ? (
            <button type="button" onClick={() => handleDone('inventory_only')} disabled={(!panelResolved && !skipUnmapped) || saving}
              style={{
                background: ((panelResolved || skipUnmapped) && !saving) ? '#2c7a4b' : '#D4C4B0',
                color: ((panelResolved || skipUnmapped) && !saving) ? '#fff' : '#A89890',
                border: 'none', borderRadius: 8, padding: '8px 22px',
                cursor: ((panelResolved || skipUnmapped) && !saving) ? 'pointer' : 'not-allowed',
                fontSize: '0.88rem', fontWeight: 700, fontFamily: FONT,
              }}>
              {saving ? 'Saving…' : '✓ Save Category Assignments'}
            </button>
          ) : (
          <button type="button" onClick={handleNext} disabled={!step1NextEnabled || saving}
            style={{
              background: step1NextEnabled && !saving ? BROWN : '#D4C4B0',
              color: step1NextEnabled && !saving ? CREAM : '#A89890',
              border: 'none', borderRadius: 8, padding: '8px 22px',
              cursor: step1NextEnabled && !saving ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', fontWeight: 700, fontFamily: FONT,
            }}>
            {saving ? 'Setting up…' : 'Next →'}
          </button>
          )
        ) : (
          <button type="button" onClick={handleDone} disabled={!salesTracking || saving}
            style={{
              background: salesTracking && !saving ? '#2c7a4b' : '#D4C4B0',
              color: salesTracking && !saving ? '#fff' : '#A89890',
              border: 'none', borderRadius: 8, padding: '8px 22px',
              cursor: salesTracking && !saving ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', fontWeight: 700, fontFamily: FONT,
            }}>
            {saving ? 'Finishing…' : '✓ Finish Setup'}
          </button>
        )}
      </div>
    </WizardModal>
  );
};

export default PNLSetupWizard;
